import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import { 
  Search, Map, Trophy, User, MapPin, ChevronRight, Users, Beer, Route,
  Star, Navigation, Loader2, MessageSquare, Camera, X, RefreshCw
} from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import axios from "axios";
import { toast } from "sonner";

const Discover = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("routes");
  const [trips, setTrips] = useState([]);
  const [bars, setBars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [barsLoading, setBarsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(false);

  // Bar detail modal state
  const [selectedBar, setSelectedBar] = useState(null);
  const [showBarDetail, setShowBarDetail] = useState(false);
  const [loadingBarDetail, setLoadingBarDetail] = useState(false);

  // Comment form state
  const [commentText, setCommentText] = useState("");
  const [commentRating, setCommentRating] = useState(0);
  const [addingComment, setAddingComment] = useState(false);
  const [barPhotos, setBarPhotos] = useState([]);
  const [addingPhotos, setAddingPhotos] = useState(false);

  // Get user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setLocationError(false);
        },
        (error) => {
          console.error("Geolocation error:", error);
          // Default to LA if no location
          setCurrentLocation({ lat: 34.0522, lng: -118.2437 });
          setLocationError(true);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setCurrentLocation({ lat: 34.0522, lng: -118.2437 });
      setLocationError(true);
    }
  }, []);

  // Fetch trips on mount
  useEffect(() => {
    fetchTrips();
  }, []);

  // Fetch bars when location available or tab switches
  useEffect(() => {
    if (currentLocation && activeTab === "bars") {
      fetchBars();
    }
  }, [currentLocation, activeTab]);

  const fetchTrips = async () => {
    try {
      const tripsRes = await axios.get(`${API}/trips/discover`);
      setTrips(tripsRes.data.trips || []);
    } catch (error) {
      console.error("Failed to load trips:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBars = async () => {
    if (!currentLocation) return;
    setBarsLoading(true);
    try {
      const response = await axios.get(
        `${API}/bars?lat=${currentLocation.lat}&lng=${currentLocation.lng}&radius=50`
      );
      setBars(response.data.bars || []);
    } catch (error) {
      console.error("Failed to fetch bars:", error);
    } finally {
      setBarsLoading(false);
    }
  };

  const handleBarClick = async (bar) => {
    setLoadingBarDetail(true);
    setShowBarDetail(true);
    try {
      const response = await axios.get(`${API}/bars/${bar.bar_id}`);
      setSelectedBar(response.data);
    } catch (error) {
      toast.error("Failed to load bar details");
      setShowBarDetail(false);
    } finally {
      setLoadingBarDetail(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    setAddingComment(true);
    try {
      await axios.post(`${API}/bars/${selectedBar.bar_id}/comments`, {
        text: commentText,
        rating: commentRating || null
      });

      toast.success("Review added!");
      setCommentText("");
      setCommentRating(0);

      // Refresh bar details
      const response = await axios.get(`${API}/bars/${selectedBar.bar_id}`);
      setSelectedBar(response.data);
    } catch (error) {
      toast.error("Failed to add review");
    } finally {
      setAddingComment(false);
    }
  };

  const handleAddPhotos = async () => {
    if (barPhotos.length === 0) return;

    setAddingPhotos(true);
    try {
      await axios.post(`${API}/bars/${selectedBar.bar_id}/photos`, {
        photos: barPhotos
      });

      toast.success("Photos added!");
      setBarPhotos([]);

      // Refresh bar details
      const response = await axios.get(`${API}/bars/${selectedBar.bar_id}`);
      setSelectedBar(response.data);
    } catch (error) {
      toast.error("Failed to add photos");
    } finally {
      setAddingPhotos(false);
    }
  };

  const openGoogleMapsNavigation = (bar) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${bar.coordinates.lat},${bar.coordinates.lng}&travelmode=driving`;
    window.open(url, '_blank');
  };

  const renderStars = (rating, interactive = false, onRate = null) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && onRate && onRate(star)}
            className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
          >
            <Star
              className={`w-4 h-4 ${star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}`}
            />
          </button>
        ))}
      </div>
    );
  };

  const filteredTrips = trips.filter(trip =>
    trip.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    trip.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredBars = bars.filter(bar =>
    bar.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    bar.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'dive_bar': return <Beer className="w-3 h-3" />;
      case 'restaurant': return <span className="text-[10px]">🍔</span>;
      case 'landmark': return <span className="text-[10px]">🗿</span>;
      case 'hotel': return <span className="text-[10px]">🏨</span>;
      case 'gas_station': return <span className="text-[10px]">⛽</span>;
      default: return <MapPin className="w-3 h-3" />;
    }
  };

  return (
    <div className="mobile-container asphalt-texture min-h-screen pb-24">
      {/* Header */}
      <div className="p-4 pt-6">
        <div className="flex items-center gap-2 mb-2">
          <Beer className="w-6 h-6 text-bronze" />
          <h1 className="font-oswald text-2xl font-bold uppercase">Discover</h1>
        </div>
        <p className="text-gray-500 text-sm mb-4">Community rides and dive bars</p>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <Button
            data-testid="tab-rides"
            variant={activeTab === "routes" ? "default" : "outline"}
            className={`flex-1 h-10 font-oswald uppercase ${
              activeTab === "routes" 
                ? "bg-bronze hover:bg-bronze-600" 
                : "border-white/20 hover:bg-white/5"
            }`}
            onClick={() => setActiveTab("routes")}
          >
            <Route className="w-4 h-4 mr-2" />
            Rides
          </Button>
          <Button
            data-testid="tab-dives"
            variant={activeTab === "bars" ? "default" : "outline"}
            className={`flex-1 h-10 font-oswald uppercase ${
              activeTab === "bars" 
                ? "bg-bronze hover:bg-bronze-600" 
                : "border-white/20 hover:bg-white/5"
            }`}
            onClick={() => setActiveTab("bars")}
          >
            <Beer className="w-4 h-4 mr-2" />
            Dives
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            data-testid="search-input"
            type="text"
            placeholder={activeTab === "routes" ? "Search rides..." : "Search dives..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 bg-[#121212] border-white/10 focus:border-bronze placeholder:text-gray-600"
          />
        </div>

        {/* Routes Tab */}
        {activeTab === "routes" && (
          <>
            {/* Featured Banner */}
            {trips.length > 0 && (
              <div 
                className="relative rounded-xl overflow-hidden mb-6 cursor-pointer group"
                onClick={() => navigate(`/trip/${trips[0].trip_id}`)}
              >
                <img
                  src="https://images.unsplash.com/photo-1632166122058-f906281191de?w=600"
                  alt="Featured route"
                  className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="text-xs text-bronze uppercase tracking-wide">Featured Route</span>
                  <h3 className="font-oswald text-lg font-bold uppercase mt-1">{trips[0].title}</h3>
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {trips[0].stops?.length || 0} stops
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {trips[0].creator?.name || 'Anonymous'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Routes List */}
            <div className="mb-4">
              <h2 className="font-oswald text-lg uppercase tracking-wide mb-3">Community Rides</h2>

              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="glass rounded-xl p-4 animate-pulse">
                      <div className="h-5 bg-white/10 rounded w-1/2 mb-2" />
                      <div className="h-3 bg-white/10 rounded w-3/4" />
                    </div>
                  ))}
                </div>
              ) : filteredTrips.length === 0 ? (
                <div className="glass rounded-xl p-6 text-center">
                  <Route className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">No routes found</p>
                  <p className="text-gray-600 text-xs mt-1">
                    {searchQuery ? 'Try a different search' : 'Be the first to share a ride!'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredTrips.map((trip) => (
                    <div
                      key={trip.trip_id}
                      data-testid={`discover-trip-${trip.trip_id}`}
                      className="glass rounded-xl p-4 cursor-pointer hover:border-bronze/50 transition-colors"
                      onClick={() => navigate(`/trip/${trip.trip_id}`)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h3 className="font-oswald font-semibold uppercase">{trip.title}</h3>
                          {trip.description && (
                            <p className="text-gray-500 text-xs mt-1 line-clamp-1">{trip.description}</p>
                          )}
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-600" />
                      </div>
                      
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            <span>{trip.stops?.length || 0} stops</span>
                          </div>
                          {trip.stops?.length > 0 && (
                            <div className="flex items-center gap-1">
                              {trip.stops.slice(0, 3).map((stop, idx) => (
                                <span key={idx} className={`category-${stop.category} px-1 py-0.5 rounded text-[10px]`}>
                                  {getCategoryIcon(stop.category)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {trip.creator?.name || 'Anonymous'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Dives Tab */}
        {activeTab === "bars" && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="font-oswald text-lg uppercase tracking-wide">Nearby Dives</h2>
                {locationError && (
                  <p className="text-xs text-amber-500">Using default location</p>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400"
                onClick={fetchBars}
                disabled={barsLoading}
              >
                <RefreshCw className={`w-4 h-4 ${barsLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {barsLoading && bars.length === 0 ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="glass rounded-xl p-4 animate-pulse">
                    <div className="flex gap-3">
                      <div className="w-16 h-16 rounded-lg bg-white/10" />
                      <div className="flex-1">
                        <div className="h-5 bg-white/10 rounded w-1/2 mb-2" />
                        <div className="h-3 bg-white/10 rounded w-3/4" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredBars.length === 0 ? (
              <div className="glass rounded-xl p-6 text-center">
                <Beer className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No dives found nearby</p>
                <p className="text-gray-600 text-xs mt-1">
                  {searchQuery ? 'Try a different search' : 'Be the first to add one!'}
                </p>
                <Button
                  data-testid="add-bar-btn"
                  className="mt-4 bg-bronze hover:bg-bronze-600 font-oswald uppercase"
                  onClick={() => navigate('/bars')}
                >
                  Add a Dive
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredBars.map((bar) => (
                  <div
                    key={bar.bar_id}
                    data-testid={`bar-${bar.bar_id}`}
                    className="glass rounded-xl p-4 cursor-pointer hover:border-bronze/50 transition-colors"
                    onClick={() => handleBarClick(bar)}
                  >
                    <div className="flex gap-3">
                      {bar.photos?.[0] ? (
                        <img 
                          src={bar.photos[0]} 
                          alt={bar.name}
                          className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-bronze/20 flex items-center justify-center flex-shrink-0">
                          <Beer className="w-6 h-6 text-bronze" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-oswald font-semibold uppercase truncate">{bar.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          {bar.avg_rating && (
                            <div className="flex items-center gap-1">
                              <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                              <span className="text-xs text-gray-400">{bar.avg_rating.toFixed(1)}</span>
                            </div>
                          )}
                          {bar.distance && (
                            <span className="text-xs text-gray-500">
                              {bar.distance.toFixed(1)} mi
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 mt-1 truncate">{bar.address}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-600 flex-shrink-0 self-center" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bar Detail Modal */}
      <Dialog open={showBarDetail} onOpenChange={setShowBarDetail}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-sm mx-4 max-h-[90vh] overflow-y-auto p-0">
          {loadingBarDetail ? (
            <div className="p-8 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-bronze" />
            </div>
          ) : selectedBar && (
            <>
              {/* Photos */}
              {selectedBar.photos?.length > 0 ? (
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={selectedBar.photos[0]}
                    alt={selectedBar.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
                  {selectedBar.photos.length > 1 && (
                    <div className="absolute bottom-2 right-2 bg-black/60 px-2 py-1 rounded text-xs">
                      +{selectedBar.photos.length - 1} more
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-32 bg-gradient-to-b from-bronze/20 to-transparent flex items-center justify-center">
                  <Beer className="w-16 h-16 text-bronze/50" />
                </div>
              )}

              <div className="p-4 space-y-4">
                {/* Header */}
                <div>
                  <h2 className="font-oswald text-2xl font-bold">{selectedBar.name}</h2>
                  <p className="text-sm text-gray-400 mt-1">{selectedBar.address}</p>
                  
                  <div className="flex items-center gap-4 mt-3">
                    {selectedBar.avg_rating && (
                      <div className="flex items-center gap-2">
                        {renderStars(Math.round(selectedBar.avg_rating))}
                        <span className="text-sm text-gray-400">({selectedBar.comment_count || 0})</span>
                      </div>
                    )}
                    {selectedBar.distance && (
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        <Navigation className="w-4 h-4" />
                        {selectedBar.distance.toFixed(1)} mi
                      </span>
                    )}
                  </div>
                </div>

                {/* Description */}
                {selectedBar.description && (
                  <p className="text-gray-300 text-sm">{selectedBar.description}</p>
                )}

                {/* Photo Gallery */}
                {selectedBar.photos?.length > 1 && (
                  <div>
                    <h3 className="text-xs text-gray-400 uppercase mb-2">Photos</h3>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {selectedBar.photos.map((photo, idx) => (
                        <img
                          key={idx}
                          src={photo}
                          alt=""
                          className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Add Photos */}
                {user && (
                  <div className="space-y-2">
                    <h3 className="text-xs text-gray-400 uppercase flex items-center gap-2">
                      <Camera className="w-3 h-3" />
                      Add Your Photos
                    </h3>
                    <ImageUpload
                      images={barPhotos}
                      onImagesChange={setBarPhotos}
                      maxImages={5}
                      folder={`bars/${selectedBar.bar_id}`}
                    />
                    {barPhotos.length > 0 && (
                      <Button
                        size="sm"
                        className="bg-bronze hover:bg-bronze-600"
                        onClick={handleAddPhotos}
                        disabled={addingPhotos}
                      >
                        {addingPhotos ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload Photos"}
                      </Button>
                    )}
                  </div>
                )}

                {/* Reviews */}
                <div>
                  <h3 className="text-xs text-gray-400 uppercase mb-3 flex items-center gap-2">
                    <MessageSquare className="w-3 h-3" />
                    Reviews ({selectedBar.comment_count || 0})
                  </h3>

                  {/* Add Review */}
                  {user && (
                    <div className="glass rounded-lg p-3 mb-4 space-y-3">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Your Rating</p>
                        {renderStars(commentRating, true, setCommentRating)}
                      </div>
                      <Textarea
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder="Share your experience..."
                        className="bg-[#0a0a0a] border-white/10 min-h-[60px]"
                      />
                      <Button
                        size="sm"
                        className="bg-bronze hover:bg-bronze-600"
                        onClick={handleAddComment}
                        disabled={addingComment}
                      >
                        {addingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post Review"}
                      </Button>
                    </div>
                  )}

                  {/* Reviews List */}
                  <div className="space-y-3">
                    {selectedBar.comments?.map((comment) => (
                      <div key={comment.comment_id} className="border-b border-white/5 pb-3">
                        <div className="flex items-center gap-2 mb-2">
                          {comment.user_picture ? (
                            <img src={comment.user_picture} alt="" className="w-6 h-6 rounded-full" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-bronze/20 flex items-center justify-center">
                              <span className="text-xs text-bronze">{comment.user_name?.[0]}</span>
                            </div>
                          )}
                          <span className="text-sm font-medium">{comment.user_name}</span>
                          {comment.rating && (
                            <div className="flex ml-auto">
                              {renderStars(comment.rating)}
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-gray-300">{comment.text}</p>
                      </div>
                    ))}

                    {(!selectedBar.comments || selectedBar.comments.length === 0) && (
                      <p className="text-gray-500 text-sm text-center py-4">
                        No reviews yet. Be the first!
                      </p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button
                    className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                    onClick={() => openGoogleMapsNavigation(selectedBar)}
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    Navigate
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 border-white/20"
                    onClick={() => setShowBarDetail(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation */}
      <div className="bottom-nav glass safe-bottom">
        <div className="flex justify-around py-3">
          <button
            data-testid="nav-dashboard"
            className="flex flex-col items-center gap-1 text-gray-500 hover:text-white transition-colors"
            onClick={() => user ? navigate('/dashboard') : navigate('/login')}
          >
            <Map className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wide">My Trips</span>
          </button>
          <button
            data-testid="nav-discover"
            className="flex flex-col items-center gap-1 text-bronze"
          >
            <Beer className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wide">Discover</span>
          </button>
          <button
            data-testid="nav-leaderboard"
            className="flex flex-col items-center gap-1 text-gray-500 hover:text-white transition-colors"
            onClick={() => navigate('/leaderboard')}
          >
            <Trophy className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wide">Ranks</span>
          </button>
          <button
            data-testid="nav-profile"
            className="flex flex-col items-center gap-1 text-gray-500 hover:text-white transition-colors"
            onClick={() => user ? navigate('/profile') : navigate('/login')}
          >
            <User className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wide">Profile</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Discover;
